import { Tick02Icon } from '@hugeicons/react'
import clsx from 'clsx'

import { useBlocker } from '@/shared/hooks/useBlocker'
import { useNavigation } from '@/shared/hooks/useNavigation'
import { useUserProgress } from '@/shared/hooks/useUserProgress'
import { Button } from '@/shared/ui/Button'
import type { ButtonVariant } from '@/shared/ui/Button'
import { RewardBadge } from '@/shared/ui/RewardBadge'
import type { JourneyWithUnderscores, LevelWithUnderscores } from '@/types/journey'

import { LevelThumb } from './LevelThumb'
import { getBlockHintText } from '../lib/blockHintText'
import { getLevelUiStatus } from '../lib/levelUiStatus'
import {
  getFirstPendingLevelRef,
  getPendingLevelsAlertMessage,
  getPendingLevelsHint
} from '../lib/pendingLevelsHint'

/* Formato horizontal único — substituiu o card de grid (thumb em cima,
   vertical, 2 por linha) que existia só pra fases sem sequência obrigatória
   (`stage.is_open`). O grid saiu de uso; este é o mesmo card já usado nas
   fases sequenciais, sem o `TimelineRow` (dot + conector) ao redor. */
const TIMELINE_CONTENT_CLASS: Record<string, string> = {
  success: '',
  progress: 'border-l-4 border-status-progress shadow-[0_0_0_2px_rgba(255,152,0,0.12)]',
  available: 'border-l-4 border-brand',
  locked: 'border-2 border-[#d8dae3] cursor-default'
}

interface LevelCardProps {
  journey: JourneyWithUnderscores
  level: LevelWithUnderscores
}

export function LevelCard({ journey, level }: LevelCardProps) {
  const { navigateTo } = useNavigation()
  const { checkBlockCondition, showBlockAlert } = useBlocker()
  const { levelWasFinished } = useUserProgress()
  const blockResult = checkBlockCondition(level._blockCondition)

  // A contagem não sai de `blockResult.unmet`: ele mistura condições de missão,
  // tempo e blockRedo, e filtrá-lo por id seria reconstruir o que o progresso
  // já responde direto.
  const pendingLevelsHint = getPendingLevelsHint(
    level.unlock_after_levels,
    levelWasFinished
  )

  const uiStatus = getLevelUiStatus({
    level,
    blockResult,
    currentLevelUid: journey?._currentLevel?.uid
  })

  const handleClick = () => {
    if (blockResult.unlocked) {
      navigateTo(`/jornadas/${journey.uid}/niveis/${level.uid}/iniciar`)
      return
    }

    if (pendingLevelsHint) {
      const resolveLevel = (id: number) => {
        for (const stage of journey._stages ?? []) {
          const required = stage._levels?.find(l => l.id === id)
          if (required) {
            return {
              id: required.id,
              uid: required.uid,
              name: required.name
            }
          }
        }

        return undefined
      }

      const firstPending = getFirstPendingLevelRef(
        level.unlock_after_levels,
        levelWasFinished,
        resolveLevel
      )

      if (firstPending) {
        const pendingNames = (level.unlock_after_levels ?? [])
          .filter(id => !levelWasFinished(id))
          .map(id => resolveLevel(id)?.name)
          .filter((name): name is string => !!name)

        showBlockAlert({
          type: 'resource',
          resource: {
            type: 'level',
            id: firstPending.id,
            uid: firstPending.uid,
            name: firstPending.name
          },
          condition: 'finished',
          message: getPendingLevelsAlertMessage(
            pendingLevelsHint,
            pendingNames
          )
        })
        return
      }
    }

    showBlockAlert(blockResult.firstUnmet)
  }

  const ctaLabel =
    uiStatus === 'available'
      ? 'Iniciar'
      : uiStatus === 'progress'
        ? 'Continuar'
        : null

  const ctaVariant: ButtonVariant =
    uiStatus === 'available' ? 'primary' : 'progress'

  const hasRewards =
    (level.total_point_reward ?? 0) > 0 || (level.total_coin_reward ?? 0) > 0

  const lockHint =
    uiStatus === 'locked'
      ? (pendingLevelsHint ??
        (blockResult.firstUnmet
          ? getBlockHintText(blockResult.firstUnmet)
          : 'Complete o nível anterior'))
      : null

  const iconUrl = level.icon?.file?.url ?? null

  return (
    <div
      className={clsx(
        'flex-1 min-w-0 rounded-[0.625rem] px-[0.625rem] py-2 bg-white transition-[background,box-shadow] duration-150 ease',
        uiStatus !== 'locked' && 'cursor-pointer hover:bg-[#fafbfc]',
        TIMELINE_CONTENT_CLASS[uiStatus]
      )}
      onClick={handleClick}
    >
      <div className="flex items-center gap-[0.625rem]">
        <LevelThumb
          url={iconUrl}
          size={42}
          locked={uiStatus === 'locked'}
          alt=""
        />

        <div className="flex-1 min-w-0 flex flex-col gap-1">
          <span className="text-sm font-bold text-gray-800 block leading-[1.3]">
            {level.name}
          </span>
          <div className="flex items-center flex-wrap gap-1">
            {hasRewards && (
              <RewardBadge.Group>
                {(level.total_point_reward ?? 0) > 0 && (
                  <RewardBadge
                    type="xp"
                    value={level.total_point_reward}
                    variant="inline"
                  />
                )}
                {(level.total_coin_reward ?? 0) > 0 && (
                  <RewardBadge
                    type="coins"
                    value={level.total_coin_reward}
                    variant="inline"
                  />
                )}
              </RewardBadge.Group>
            )}
          </div>
          {lockHint && (
            <span className="text-2xs text-gray-700 leading-[1.35]">
              {lockHint}
            </span>
          )}
        </div>

        {uiStatus === 'success' ? (
          <div
            className="shrink-0 w-8 h-8 rounded-[0.625rem] bg-status-success flex items-center justify-center shadow-[0_2px_6px_rgba(22,163,74,0.28)]"
            aria-label="Concluído"
          >
            <Tick02Icon size={16} color="#fff" strokeWidth={3} />
          </div>
        ) : ctaLabel ? (
          <div className="shrink-0 flex items-center">
            <Button
              variant={ctaVariant}
              size="sm"
              showTrailingIcon={false}
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation()
                handleClick()
              }}
            >
              {ctaLabel}
            </Button>
          </div>
        ) : uiStatus === 'locked' ? (
          <Button
            variant="secondary"
            size="sm"
            showTrailingIcon={false}
            aria-label="Bloqueado — toque para saber como desbloquear"
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation()
              handleClick()
            }}
          >
            Bloqueado
          </Button>
        ) : null}
      </div>
    </div>
  )
}
